import React from 'react';
import { EasingFunction, Easing } from './BaseEffect';

/**
 * Layer types that can be targeted by container effects
 */
export type LayerType = 'background' | 'scene' | 'overlay' | 'overlayTransition' | 'textOverlay' | 'caption' | 'branding';

/**
 * Configuration interface for BaseContainerEffect constructor
 */
export interface ContainerEffectConfig {
    targetLayers: LayerType[];
    excludeLayers?: LayerType[];
    from: number;
    to: number;
    duration: number;
    at: number;
    easing?: EasingFunction;
}

/**
 * BaseContainerEffect - Abstract base class for all container-based effects
 * 
 * Container effects group multiple layers into a single container and apply
 * transformations to the entire container, ensuring layers move/transform
 * together as a unified unit.
 * 
 * Key benefits:
 * - Natural spatial relationships (zoom/rotate maintain layer positioning)
 * - Better performance (single GPU operation per container)
 * - Unified visual behavior (layers act as cohesive group)
 * 
 * This replaces the previous "multi-track" system which applied effects
 * individually to each layer, losing the benefits of grouped transformations.
 */
export abstract class BaseContainerEffect {
    protected readonly targetLayers: readonly LayerType[];
    protected readonly excludeLayers: readonly LayerType[];
    protected readonly from: number;
    protected readonly to: number;
    protected readonly duration: number;
    protected readonly at: number;
    protected readonly easing: EasingFunction;

    constructor(config: ContainerEffectConfig) {
        this.targetLayers = [...config.targetLayers];
        this.excludeLayers = [...(config.excludeLayers ?? [])];
        this.from = config.from;
        this.to = config.to;
        this.duration = config.duration;
        this.at = config.at;
        this.easing = config.easing || Easing.easeOutCubic;
    }

    /**
     * Calculate effect progress based on current time
     */
    calculateProgress(currentTime: number): number {
        // Handle non-positive durations gracefully
        if (this.duration <= 0) {
            return currentTime < this.at ? 0 : 1;
        }
        if (currentTime < this.at) return 0;
        if (currentTime >= this.at + this.duration) return 1;
        const localProgress = (currentTime - this.at) / this.duration;
        const clamped = Math.max(0, Math.min(1, localProgress));
        return this.easing(clamped);
    }

    /**
     * Determine if a specific layer should be affected by this effect
     */
    shouldApplyToLayer(layerType: LayerType): boolean {
        if (this.excludeLayers.includes(layerType)) return false;
        return this.targetLayers.includes(layerType);
    }

    /**
     * Get list of layers that will actually be affected (after exclusions)
     */
    getAffectedLayers(): LayerType[] {
        return this.targetLayers.filter(layer => 
            !this.excludeLayers.includes(layer)
        );
    }

    /**
     * Check if this effect is currently active at the given time
     */
    isActiveAt(currentTime: number): boolean {
        return currentTime >= this.at && currentTime < this.at + this.duration;
    }

    /**
     * Get effect timing information
     */
    getTiming(): { startAt: number; endAt: number; duration: number } {
        return {
            startAt: this.at,
            endAt: this.at + this.duration,
            duration: this.duration
        };
    }

    // Abstract methods that each effect must implement

    /**
     * Generate CSS styles for the container based on current progress
     * This is where effect-specific transformations are applied
     */
    abstract getContainerStyle(progress: number): React.CSSProperties;

    /**
     * Return a unique identifier for this effect type
     * Used for debugging, logging, and effect management
     */
    abstract getEffectType(): string;

    /**
     * Optional method for effects that need cleanup
     */
    dispose?(): void;
}